import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SITE_URL, DEFAULT_KEYWORDS } from './seoConfig';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  article?: boolean;
  publishedDate?: string;
  modifiedDate?: string;
  authorName?: string;
  ogImage?: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  document.head.querySelector(selector)?.remove();
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  article = false,
  publishedDate,
  modifiedDate,
  authorName = 'AllChinaBuy Spreadsheet Team',
  ogImage,
}: SEOHeadProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const siteTitle = title ? `${title} | ${t('siteName')}` : t('siteName');
    const metaDescription = description || t('tagline');
    const metaKeywords = keywords || DEFAULT_KEYWORDS;
    const canonical = canonicalUrl || `${SITE_URL}/`;

    document.title = siteTitle;

    upsertMeta('name', 'description', metaDescription);
    upsertMeta('name', 'keywords', metaKeywords);
    upsertMeta('name', 'author', authorName);
    upsertMeta('name', 'robots', 'index, follow');

    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:title', siteTitle);
    upsertMeta('property', 'og:description', metaDescription);
    upsertMeta('property', 'og:type', article ? 'article' : 'website');
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:site_name', t('siteName'));

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', siteTitle);
    upsertMeta('name', 'twitter:description', metaDescription);

    if (ogImage) {
      upsertMeta('property', 'og:image', ogImage);
      upsertMeta('name', 'twitter:image', ogImage);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
    }

    if (article) {
      if (publishedDate) upsertMeta('property', 'article:published_time', publishedDate);
      if (modifiedDate) upsertMeta('property', 'article:modified_time', modifiedDate);
    } else {
      removeMeta('property', 'article:published_time');
      removeMeta('property', 'article:modified_time');
    }
  }, [title, description, keywords, canonicalUrl, article, publishedDate, modifiedDate, authorName, ogImage, t]);

  return null;
}